import React, { useEffect, useState } from "react";
import { api, money } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Truck, CreditCard } from "lucide-react";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";
const EMPTY = { name: "", phone: "", email: "", address: "", account_number: "", credit_limit: 0, rep_name: "", rep_phone: "", rep_email: "", notes: "" };

export default function Suppliers() {
  const { t } = useLang();
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/suppliers").then(setSuppliers).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...s }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error(t("nameRequired"));
    try {
      const payload = { ...form, credit_limit: parseFloat(form.credit_limit) || 0 };
      if (editing) await api.put(`/suppliers/${editing.id}`, payload);
      else await api.post("/suppliers", payload);
      toast.success(t("saved"));
      setOpen(false); load();
    } catch { toast.error(t("error")); }
  };

  const remove = async (s) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.del(`/suppliers/${s.id}`);
    load();
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("suppliers")}</h1>
        <button data-testid="new-supplier-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white text-sm font-medium px-4 py-2.5 hover:bg-slate-800 transition-colors">
          <Plus className="w-4 h-4" /> {t("newSupplier")}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">
                <th className="text-left px-4 py-3">{t("name")}</th>
                <th className="text-left px-4 py-3">{t("ourAccount")}</th>
                <th className="text-left px-4 py-3">{t("contact")}</th>
                <th className="text-left px-4 py-3">{t("accountManager")}</th>
                <th className="text-right px-4 py-3">{t("creditLimit")}</th>
                <th className="text-right px-4 py-3">{t("usedThisMonth")}</th>
                <th className="text-left px-4 py-3">{t("availableCredit")}</th>
                <th className="text-right px-4 py-3">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map((s) => {
                const limit = parseFloat(s.credit_limit) || 0;
                const used = s.used_this_month || 0;
                const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
                const danger = limit > 0 && used / limit >= 0.85;
                return (
                  <tr key={s.id} data-testid={`supplier-row-${s.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 flex items-center gap-2"><Truck className="w-3.5 h-3.5 text-slate-400" />{s.name}</div>
                      {s.address && <div className="text-xs text-slate-400 ml-5">{s.address}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {s.account_number ? <code className="text-xs font-mono bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded inline-flex items-center gap-1"><CreditCard className="w-3 h-3" />{s.account_number}</code> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      {s.phone && <div>{s.phone}</div>}
                      {s.email && <div className="text-slate-400">{s.email}</div>}
                      {!s.phone && !s.email && <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs" data-testid={`supplier-rep-${s.id}`}>
                      {s.rep_name || s.rep_phone || s.rep_email ? (
                        <div>
                          {s.rep_name && <div className="font-medium text-slate-800">{s.rep_name}</div>}
                          {s.rep_phone && <a href={`tel:${s.rep_phone}`} className="text-blue-600 block">{s.rep_phone}</a>}
                          {s.rep_email && <a href={`mailto:${s.rep_email}`} className="text-slate-400 block">{s.rep_email}</a>}
                        </div>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-medium tabular-nums">{limit > 0 ? money(limit) : <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-700">{money(used)}</td>
                    <td className="px-4 py-3 min-w-[160px]">
                      {limit > 0 ? (
                        <div data-testid={`supplier-credit-${s.id}`}>
                          <div className={`text-xs font-bold tabular-nums ${danger ? "text-red-600" : "text-emerald-700"}`}>{money(s.available)}</div>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
                            <div className={`h-full ${danger ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      ) : <span className="text-xs text-slate-400">{t("noLimit")}</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button data-testid={`edit-supplier-${s.id}`} onClick={() => openEdit(s)} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"><Pencil className="w-4 h-4" /></button>
                        <button data-testid={`delete-supplier-${s.id}`} onClick={() => remove(s)} className="p-1.5 rounded-md text-slate-500 hover:bg-red-50 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {suppliers.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">{t("noSuppliers")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div data-testid="supplier-modal" className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">{editing ? t("editSupplier") : t("newSupplier")}</h2>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("name")} *</label>
                <input data-testid="supplier-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Home Depot" className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("ourAccount")}</label>
                  <input data-testid="supplier-account" value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} placeholder="PRO-88231" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("creditLimit")} ($)</label>
                  <input data-testid="supplier-limit" type="number" step="0.01" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("phone")}</label>
                  <input data-testid="supplier-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("email")}</label>
                  <input data-testid="supplier-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("address")}</label>
                <input data-testid="supplier-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} />
              </div>
              <div className="border-t border-slate-200 pt-4">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">{t("accountManager")}</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">{t("name")}</label>
                    <input data-testid="supplier-rep-name" value={form.rep_name || ""} onChange={(e) => setForm({ ...form, rep_name: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">{t("phone")}</label>
                    <input data-testid="supplier-rep-phone" value={form.rep_phone || ""} onChange={(e) => setForm({ ...form, rep_phone: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">{t("email")}</label>
                    <input data-testid="supplier-rep-email" value={form.rep_email || ""} onChange={(e) => setForm({ ...form, rep_email: e.target.value })} className={inputCls} />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{t("notes")}</label>
                <textarea data-testid="supplier-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className={inputCls} />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <button onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-50">{t("cancel")}</button>
              <button data-testid="supplier-save" onClick={save} className="rounded-lg px-4 py-2 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800">{t("save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
